<svelte:options
  customElement={{
    tag: "oc-switch-v2",
    extend: window.__components.extend({
      formAssociated: true,
      inputBehavior: true,
    }),
    props: {
      name: { type: "String", reflect: true },
      size: { type: "String" },
      labelPlacement: { type: "String", attribute: "label-placement" },
      disabled: { type: "Boolean", reflect: true },
      value: { type: "String" },
      checked: { type: "Boolean" },
      loading: { type: "Boolean" },
      ocAriaLabel: { type: "String", attribute: "oc-aria-label" },
      fitContent: { type: "Boolean", attribute: "fit-content" },
    },
  }}
/>

<script lang="ts">
  import { fade } from "svelte/transition";

  import { exposeStatesForCSS } from "@otto-ec/otto-components-utils/utils";
  import { useSlots } from "../../../common/utils/useSlots.svelte";
  import type { Props } from "./SwitchV2.types.js";
  import {
    implicitSubmit,
    refireNonComposableNativeEvent,
    stopLabelClickPropagation,
  } from "../../../common/actions";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";

  let {
    /*                                                                                   */
    name = undefined,
    size = "100",
    labelPlacement = undefined,
    disabled = false,
    checked = false,
    loading = false,
    ocAriaLabel = undefined,
    value = "on",
    internals,
    fitContent,
  }: Props & {
    internals: ElementInternals;
  } = $props();

  const Host = $host();

  const slots = useSlots(Host);

  $effect(() => {
    internals.setFormValue(checked ? value : null);
  });

  export function resetForm(): void {
    /*                            */
    checked = Host.hasAttribute("checked");
  }

  $effect(() => {
    exposeStatesForCSS(Host, { checked });
  });
</script>

<label
  class="switch switch--size-{size}"
  class:switch--loading={loading}
  class:switch--checked={checked}
  class:switch--disabled={disabled}
  class:switch--fit-content={fitContent}
  use:stopLabelClickPropagation
>
  <!-- Left Label -->
  {#if slots.default && labelPlacement === "left"}
    <span class="label"><slot /></span>
  {/if}
  <span class="slider">
    <input
      class="input"
      type="checkbox"
      role="switch"
      aria-label={ocAriaLabel}
      aria-checked={checked}
      {disabled}
      aria-disabled={disabled || loading}
      bind:checked
      use:refireNonComposableNativeEvent={Host}
      use:implicitSubmit={internals}
    />
    <span class="handle" aria-hidden="true">
      {#if loading}
        <oc-spinner-v1
          in:fade={{ duration: 200 }}
          variant="default"
          size={/*                      */ "50"}
        ></oc-spinner-v1>
      {/if}
      <span class="checkmark"></span>
    </span>
  </span>
  <!-- Right Label -->
  {#if slots.default && labelPlacement !== "left"}
    <span class="label"><slot /></span>
  {/if}
</label>

<DefaultStyles display="block" />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";
  :global {
    $borderRadius: 1rem;

    :host {
      --visibility-state: hidden;
    }

    .input {
      all: unset;
      position: absolute;
      inset: 0;
      border-radius: $borderRadius;
    }

    /*                                                       */
    @mixin switch-slider-color($bgColor) {
      .slider {
        background-color: $bgColor;
      }
    }

    @mixin switch-handle-position-checked($size) {
      .handle {
        @if $size == 50 {
          transform: translateX(0.625rem);
        }
        @if $size == 100 {
          transform: translateX(0.875rem);
        }
      }
    }

    @mixin switch-handle-color($bgColor) {
      .handle {
        background-color: $bgColor;
      }
    }

    @mixin switch-slider-size($size) {
      .slider {
        @if $size == 50 {
          height: tokens.$oc-component-switch-50-height;
          width: tokens.$oc-component-switch-50-width;
        }
        @if $size == 100 {
          height: tokens.$oc-component-switch-100-height;
          width: tokens.$oc-component-switch-100-width;
        }
      }
    }

    @mixin handle-size($size) {
      .handle {
        @if ($size == 50) {
          width: tokens.$oc-component-switch-50-handle-size;
          height: tokens.$oc-component-switch-50-handle-size;
          transform: translateX(-0.125rem);

          .checkmark {
            width: tokens.$oc-component-switch-50-icon-size;
            height: tokens.$oc-component-switch-50-icon-size;
          }
        }
        @if $size == 100 {
          height: tokens.$oc-component-switch-100-handle-size;
          width: tokens.$oc-component-switch-100-handle-size;
          transform: translateX(-0.125rem);

          .checkmark {
            width: tokens.$oc-component-switch-100-icon-size;
            height: tokens.$oc-component-switch-100-icon-size;
          }
        }
      }
    }

    .switch {
      display: flex;
      gap: tokens.$oc-component-switch-100-gap-x;
      align-items: center;
      outline: none;
      justify-content: space-between;
      /*           */
      cursor: pointer;

      &--fit-content {
        width: fit-content;
      }

      &.switch--loading {
        cursor: progress;
      }

      &.switch--disabled {
        cursor: default;
      }

      /*      */
      /*          */
      &:not(.switch--checked) {
        @include switch-slider-color(tokens.$oc-component-switch-default-background-color);

        &.switch--disabled {
          pointer-events: none;
          /*                                                                    */
          @include switch-slider-color(tokens.$oc-component-switch-disabled-background-color);
          @include switch-handle-color(tokens.$oc-component-switch-disabled-handle-color);

          .label {
            color: tokens.$oc-component-switch-disabled-label-color;
          }
        }

        /*                                  */
        &:not(.switch--disabled):not(.switch--loading) {
          &:hover {
            @include switch-slider-color(
              tokens.$oc-component-switch-default-background-color-hover
            );
          }

          &:active {
            @include switch-slider-color(
              tokens.$oc-component-switch-default-background-color-active
            );
          }
        }
      }

      /*      */
      &.switch--checked {
        @include switch-slider-color(tokens.$oc-component-switch-checked-background-color);
        --visibility-state: visible;

        &.switch--disabled {
          pointer-events: none;
          /*                                                                    */

          @include switch-slider-color(tokens.$oc-component-switch-disabled-background-color);
          @include switch-handle-color(tokens.$oc-component-switch-disabled-handle-color);

          .label {
            color: tokens.$oc-component-switch-disabled-label-color;
          }

          /*                               */
          .slider .handle {
            .checkmark {
              background-color: tokens.$oc-component-switch-disabled-icon-color;
            }
          }
        }

        /*                              */
        &:not(.switch--disabled):not(.switch--loading) {
          &:hover {
            @include switch-slider-color(
              tokens.$oc-component-switch-checked-background-color-hover
            );
          }

          &:active {
            @include switch-slider-color(
              tokens.$oc-component-switch-checked-background-color-active
            );
          }
        }
      }

      &--size-50 {
        @include switch-slider-size(50);
        @include handle-size(50);

        .label {
          font: tokens.$oc-component-switch-50-label-font;
        }

        &.switch--checked {
          @include switch-handle-position-checked(50);
        }
      }

      &--size-100 {
        @include switch-slider-size(100);
        @include handle-size(100);

        .label {
          font: tokens.$oc-component-switch-100-label-font;
        }

        &.switch--checked {
          @include switch-handle-position-checked(100);
        }
      }

      /*     */
      .slider {
        display: block;
        position: relative;
        transition:
          background-color tokens.$oc-component-switch-transition-duration,
          opacity 0.4s;
        border-radius: $borderRadius;
        flex: 0 0 auto;

        /*                                              */
        padding: 0.25rem 0.375rem;

        /*                   */
        .handle {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: tokens.$oc-component-switch-default-handle-color;
          transition: transform tokens.$oc-component-switch-transition-duration
            tokens.$oc-component-switch-transition-easing;
          border-radius: 50%;

          .checkmark {
            mask-image: url("/assets-static/icons/pl_icon_check50.svg");
            visibility: var(--visibility-state);
            background-color: tokens.$oc-component-switch-checked-icon-color;
          }
        }
      }
    }
  }
</style>
